/**
 * Invite Code Router - Version-Compatible Routing System
 * 
 * Handles all invite code formats across different app versions:
 * - iOS 1.0.8: Legacy token format (UUID)
 * - iOS 2.0.0: New 6-digit code format
 * - Android: Same formats as iOS
 * - Web: Fallback for all formats
 * 
 * This ensures compatibility during Apple review periods and gradual rollouts.
 */

import { supabase } from './supabase';
import { shouldRedirectToMobile } from './featureFlags';

export type InviteCodeFormat = 'legacy_token' | 'new_code' | 'qr_company_location' | 'unknown';

export interface InviteCodeData {
  format: InviteCodeFormat;
  company_id?: string;
  location_id?: string;
  draft_student_id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  token?: string;
  code?: string;
}

export interface InviteRoutingDecision {
  shouldHandleWebSide: boolean;
  shouldRedirectToMobile: boolean;
  mobileDeepLink?: string;
  webRoute?: string;
  reason: string;
}

/**
 * Detect invite code format from URL parameters
 */
export function detectInviteFormat(params: URLSearchParams): InviteCodeFormat {
  const code = params.get('code');
  const token = params.get('token');
  const companyId = params.get('companyId');

  if (code && code.length === 6) {
    return 'new_code'; // 6-digit invite code (iOS 2.0.0+)
  }
  
  if (token && token.length > 30) {
    return 'legacy_token'; // UUID token (iOS 1.0.8)
  }
  
  if (companyId) {
    return 'qr_company_location'; // QR code format (all versions)
  }
  
  return 'unknown';
}

/**
 * Decode invite code and get company/location/draft info
 * Handles both new code format and legacy token format
 */
export async function decodeInviteCode(
  format: InviteCodeFormat,
  code?: string | null,
  token?: string | null
): Promise<InviteCodeData | null> {
  try {
    if (format === 'new_code' && code) {
      // New format: 6-digit code
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: codeData, error: codeError } = await (supabase.rpc as any)('decode_invite_code', {
        p_code: code.trim().toUpperCase(),
      }) as {
        data: {
          found: boolean;
          company_id: string | null;
          location_id: string | null;
          draft_student_id: string | null;
          email: string | null;
          first_name: string | null;
          last_name: string | null;
        } | null;
        error: Error | null;
      };

      if (codeError || !codeData || !codeData.found) {
        return null;
      }

      return {
        format: 'new_code',
        company_id: codeData.company_id ?? undefined,
        location_id: codeData.location_id ?? undefined,
        draft_student_id: codeData.draft_student_id ?? undefined,
        email: codeData.email ?? undefined,
        first_name: codeData.first_name ?? undefined,
        last_name: codeData.last_name ?? undefined,
        code: code.trim().toUpperCase(),
      };
    }

    if (format === 'legacy_token' && token) {
      // Legacy format: Look up draft by invite_token
      // Note: staging_import_members only has location_id, not primary_location_id
      const { data: draftData, error: draftError } = await supabase
        .from('staging_import_members')
        .select('id, company_id, location_id, email, first_name, last_name, invite_token')
        .eq('invite_token', token)
        .eq('is_archived', false)
        .in('status', ['draft', 'invited'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (draftError || !draftData) {
        return null;
      }

      return {
        format: 'legacy_token',
        company_id: draftData.company_id,
        location_id: draftData.location_id ?? undefined,
        draft_student_id: draftData.id,
        email: draftData.email ?? undefined,
        first_name: draftData.first_name ?? undefined,
        last_name: draftData.last_name ?? undefined,
        token,
      };
    }

    return null;
  } catch (err) {
    console.error('[InviteCodeRouter] Error decoding invite:', err);
    return null;
  }
}

/**
 * Determine routing decision based on invite format, feature toggles, and platform
 */
export async function routeInviteCode(
  inviteData: InviteCodeData,
  platform: 'ios' | 'android' | 'desktop'
): Promise<InviteRoutingDecision> {
  const shouldRedirect = await shouldRedirectToMobile();

  // Desktop: Always handle web-side
  if (platform === 'desktop') {
    return {
      shouldHandleWebSide: true,
      shouldRedirectToMobile: false,
      webRoute: inviteData.format === 'new_code' && inviteData.code
        ? `/signup/invite-code?code=${encodeURIComponent(inviteData.code)}`
        : inviteData.token
        ? `/draft-invite-auth?token=${encodeURIComponent(inviteData.token)}`
        : '/signup',
      reason: 'Desktop platform - web-side handling',
    };
  }

  // Mobile: Check feature toggle
  if (!shouldRedirect) {
    // Feature toggle disabled - handle web-side
    return {
      shouldHandleWebSide: true,
      shouldRedirectToMobile: false,
      webRoute: inviteData.format === 'new_code' && inviteData.code
        ? `/signup/invite-code?code=${encodeURIComponent(inviteData.code)}`
        : inviteData.token
        ? `/draft-invite-auth?token=${encodeURIComponent(inviteData.token)}`
        : '/signup',
      reason: 'Feature toggle disabled - web-side handling',
    };
  }

  // Feature toggle enabled - redirect to mobile
  const isLocalhost = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const scheme = isLocalhost ? 'rollops-dev' : 'rollops';

  let mobileDeepLink: string;
  if (inviteData.format === 'new_code' && inviteData.code) {
    // New format: rollops://join?code=ABC123 (iOS 2.0.0+)
    mobileDeepLink = `${scheme}://join?code=${encodeURIComponent(inviteData.code)}`;
  } else if (inviteData.format === 'legacy_token' && inviteData.token) {
    // Legacy format: rollops://draft-invite-auth?token=UUID (iOS 1.0.8)
    mobileDeepLink = `${scheme}://draft-invite-auth?token=${encodeURIComponent(inviteData.token)}`;
  } else {
    // Fallback: Handle web-side if format unknown
    return {
      shouldHandleWebSide: true,
      shouldRedirectToMobile: false,
      webRoute: '/signup',
      reason: 'Unknown invite format - web-side fallback',
    };
  }

  return {
    shouldHandleWebSide: false,
    shouldRedirectToMobile: true,
    mobileDeepLink,
    reason: 'Feature toggle enabled - redirecting to mobile app',
  };
}

/**
 * Main router function - handles all invite code formats
 */
export async function routeInviteRequest(
  params: URLSearchParams,
  platform: 'ios' | 'android' | 'desktop'
): Promise<InviteRoutingDecision> {
  const format = detectInviteFormat(params);
  const code = params.get('code');
  const token = params.get('token');
  const companyId = params.get('companyId');
  const locationId = params.get('locationId');

  // Handle QR code format (company_id/location_id)
  // CRITICAL: QR codes scanned from web should ALWAYS be handled web-side
  // (like draft invites) to ensure proper onboarding flow on web
  // This prevents users from being redirected to mobile app and skipping signup/onboarding
  // Mobile app handles QR codes internally without going through web redirect
  if (format === 'qr_company_location' && companyId) {
    // Always handle QR codes web-side when accessed via web URL
    // Redirect directly to onboarding page with company/location context
    // This ensures users complete signup/onboarding on web before using mobile app
    const onboardingParams = new URLSearchParams();
    onboardingParams.set('companyId', companyId);
    if (locationId) onboardingParams.set('locationId', locationId);
    
    return {
      shouldHandleWebSide: true,
      shouldRedirectToMobile: false,
      webRoute: `/guided-student-onboarding?${onboardingParams.toString()}`,
      reason: 'QR code format - always handle web-side (block mobile redirect for web QR scans)',
    };
  }

  // Decode invite code/token
  const inviteData = await decodeInviteCode(format, code, token);
  
  if (!inviteData) {
    return {
      shouldHandleWebSide: true,
      shouldRedirectToMobile: false,
      webRoute: '/signup',
      reason: 'Invalid invite code/token - web-side fallback',
    };
  }

  // Route based on format and feature toggles
  return routeInviteCode(inviteData, platform);
}

